import React from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Edit, Plus } from "lucide-react";

interface ServiceDetail {
  id: string;
  name: string;
  category: string;
  description?: string;
  status: 'active' | 'inactive' | 'pending';
  startDate?: string;
  renewalDate?: string;
}

interface ServiceDetailsCardProps {
  services: ServiceDetail[];
  onEdit?: (service: ServiceDetail) => void;
  onAdd?: () => void;
  isEditable?: boolean;
}

export function ServiceDetailsCard({ 
  services, 
  onEdit, 
  onAdd, 
  isEditable = false 
}: ServiceDetailsCardProps) {
  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active':
        return 'bg-green-100 text-green-800 border-green-200';
      case 'inactive':
        return 'bg-gray-100 text-gray-800 border-gray-200';
      case 'pending':
        return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      default:
        return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  const formatDate = (dateString?: string) => {
    if (!dateString) return '';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  // Ensure services is always an array to prevent reduce errors
  const safeServices = services || [];
  
  const groupedServices = safeServices.reduce((acc, service) => {
    if (!acc[service.category]) {
      acc[service.category] = [];
    }
    acc[service.category].push(service);
    return acc;
  }, {} as Record<string, ServiceDetail[]>);

  const activeServicesCount = safeServices.filter(s => s.status === 'active').length;

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle>Service Details</CardTitle>
          <div className="flex items-center gap-2">
            {activeServicesCount > 0 && (
              <Badge variant="outline" className="text-blue-600 border-blue-200">
                {activeServicesCount} active service{activeServicesCount !== 1 ? 's' : ''}
              </Badge>
            )}
            {isEditable && onAdd && (
              <Button
                variant="outline"
                size="sm"
                onClick={onAdd}
                className="flex items-center gap-1"
              >
                <Plus className="h-4 w-4" />
                Add Service
              </Button>
            )}
          </div>
        </div>
      </CardHeader>
      <CardContent>
        {safeServices.length === 0 ? (
          <div className="text-center py-8 text-muted-foreground">
            <p>No services configured for this organization.</p>
            {isEditable && onAdd && (
              <Button
                variant="outline"
                size="sm"
                onClick={onAdd}
                className="mt-2"
              >
                Add First Service
              </Button>
            )}
          </div>
        ) : (
          <div className="space-y-6">
            {Object.entries(groupedServices).map(([category, categoryServices]) => (
              <div key={category}>
                <h4 className="font-medium text-sm text-muted-foreground mb-3 uppercase tracking-wide">
                  {category}
                </h4>
                <div className="space-y-3">
                  {categoryServices.map((service) => (
                    <div
                      key={service.id}
                      className="border rounded-lg p-4 hover:shadow-sm transition-shadow"
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-2">
                            <h5 className="font-medium">{service.name}</h5>
                            <Badge 
                              variant="outline" 
                              className={getStatusColor(service.status)}
                            >
                              {service.status}
                            </Badge>
                          </div>
                          
                          {service.description && (
                            <p className="text-sm text-muted-foreground mb-2">
                              {service.description}
                            </p>
                          )}
                          
                          <div className="flex flex-wrap gap-4 text-xs text-muted-foreground">
                            {service.startDate && (
                              <span>
                                Started: {formatDate(service.startDate)}
                              </span>
                            )}
                            {service.renewalDate && (
                              <span>
                                Renewal: {formatDate(service.renewalDate)}
                              </span>
                            )}
                          </div>
                        </div>
                        
                        {isEditable && onEdit && (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => onEdit(service)}
                            className="flex items-center gap-1"
                          >
                            <Edit className="h-4 w-4" />
                            Edit
                          </Button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}